// Reine Loudness-Planungs-Logik für den 2-Pass-Converter (audioConvert.ts).
// Keine ffmpeg-/Browser-Abhängigkeiten → isoliert testbar.
//
// Idee (Spec: docs/truepeak-limiter-2pass.md): LUFS skaliert linear mit Gain,
// aber der Gain hebt auch die Peaks. Passt der Zielgain in die Headroom bis zum
// TP-Ceiling, reicht reiner Gain (LUFS exakt). Braucht es mehr, leiht man sich
// bis GR_BUDGET dB über die Headroom und lässt den True-Peak-Limiter die Peaks
// festhalten — näher ans Target als „linear only", ohne zu zerquetschen.

export const TARGET = { I: -14, TP: -1, LRA: 11 };
export const CEILING_SAFETY = 0.1; // dBTP-Marge unter TARGET.TP (~-0,9 dBTP real, hier ok)
export const GR_BUDGET = 6;        // max. dB, die der Limiter über die Headroom hinaus leisten darf
export const OVERSAMPLE = 96000;   // 2× — leichtes Oversampling für den True-Peak-Limiter
export const OUT_SR = 44100;

export interface GainPlan {
  /** anzuwendender Gain in dB (kann negativ sein, wenn das File zu laut ist) */
  gainDb: number;
  /** Limiter-Ceiling als lineare Amplitude, oder null wenn kein Limiter nötig */
  limitLinear: number | null;
}

/**
 * Gain fürs LUFS-Target, gedeckelt aufs Limiting-Budget; Limiter nur wenn nötig.
 * @param measuredI  gemessene Integrated Loudness (LUFS)
 * @param measuredTP gemessener True Peak (dBTP)
 */
export function planGain(measuredI: number, measuredTP: number): GainPlan {
  const ceiling = TARGET.TP - CEILING_SAFETY;
  const gainToTarget = TARGET.I - measuredI;
  const headroom = ceiling - measuredTP; // Gain, der den Peak aufs Ceiling legt
  if (gainToTarget <= headroom) {
    return { gainDb: gainToTarget, limitLinear: null }; // passt sauber → kein Limiter
  }
  const gainDb = Math.min(gainToTarget, headroom + GR_BUDGET); // bis GR_BUDGET vom Limiter leihen
  return { gainDb, limitLinear: Math.pow(10, ceiling / 20) };
}

export interface Ebur128Measurement {
  I: number;
  truePeak: number;
}

/**
 * ebur128 druckt am Ende einen Summary-Block mit „I: -x LUFS" und „Peak: -y dBFS".
 * Fehlt der Peak, wird konservativ 0 dBFS angenommen (→ maximales Limiting).
 */
export function parseEbur128(logText: string): Ebur128Measurement {
  const i = logText.match(/^\s*I:\s*(-?\d+(?:\.\d+)?)\s*LUFS/m);
  const p = logText.match(/^\s*Peak:\s*(-?\d+(?:\.\d+)?)\s*dBFS/m);
  if (!i) throw new Error('ebur128_measure_failed');
  return { I: parseFloat(i[1]), truePeak: p ? parseFloat(p[1]) : 0 };
}

/**
 * Baut den ffmpeg -af-Filtergraph für Pass 2: Gain, und — falls limitiert wird —
 * Oversampling → alimiter (kein Makeup-Gain) → Downsampling auf OUT_SR.
 */
export function buildLoudnessFilter(plan: GainPlan): string {
  let af = `volume=${plan.gainDb.toFixed(2)}dB`;
  if (plan.limitLinear !== null) {
    af +=
      `,aresample=${OVERSAMPLE}` +
      `,alimiter=level=false:limit=${plan.limitLinear.toFixed(6)}` +
      `,aresample=${OUT_SR}`;
  }
  return af;
}
